import { useEffect, useRef, useState } from "react";

const STAGES = [
  { key: "fetching", title: "Reading your catalog", detail: (s) => (s.products_count > 0 ? `${s.products_count.toLocaleString()} products and counting…` : "Connecting to your storefront…") },
  { key: "analyzing", title: "Finding what belongs together", detail: () => "Grouping categories, collections and price points…" },
  { key: "generating", title: "Designing your bundles", detail: (s) => (s.bundles_count > 0 ? `${s.bundles_count} bundles drafted…` : "Writing names, pricing kits, picking heroes…") },
  { key: "ready", title: "Your demo is ready", detail: () => "Taking you there now…" },
];

const STAGE_INDEX = { pending: 0, fetching: 0, analyzing: 1, generating: 2, ready: 3 };

export default function LoadingExperience({ statusPath, autoRedirect = true }) {
  const [status, setStatus] = useState({ status: "pending", products_count: 0, bundles_count: 0 });
  const [failed, setFailed] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const response = await fetch(statusPath, { headers: { Accept: "application/json" } });
        if (!response.ok) throw new Error(`status ${response.status}`);
        const data = await response.json();
        if (cancelled) return;

        setStatus(data);
        if (data.status === "failed") {
          setFailed(data.error_message || "Something went wrong while building the demo.");
          return;
        }
        if (data.demo_ready) {
          if (autoRedirect) setTimeout(() => { window.location.href = data.demo_path; }, 1400);
          return;
        }
        timer.current = setTimeout(poll, 1500);
      } catch {
        if (!cancelled) timer.current = setTimeout(poll, 3000);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [statusPath, autoRedirect]);

  if (failed) {
    return (
      <div className="text-center animate-pop">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blush text-xl">!</div>
        <h3 className="display text-2xl mb-2">We hit a snag</h3>
        <p className="text-ink-soft mb-6 max-w-sm mx-auto">{failed}</p>
        <button className="btn-ghost" onClick={() => window.location.reload()}>Try again</button>
      </div>
    );
  }

  const activeIndex = STAGE_INDEX[status.status] ?? 0;

  return (
    <div className="animate-pop" aria-live="polite">
      <div className="mx-auto mb-8 relative h-20 w-20" aria-hidden="true">
        <div className="absolute inset-0 rounded-full border-4 border-sky-wash" />
        <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-sky-deep animate-spin" style={{ animationDuration: "1.1s" }} />
        <div className="absolute inset-[22%] rounded-2xl bg-gradient-to-br from-sky to-sand animate-pulse-soft" />
      </div>

      <ol className="space-y-4 max-w-sm mx-auto text-left">
        {STAGES.map((stage, index) => {
          const state = index < activeIndex ? "done" : index === activeIndex ? "active" : "todo";
          return (
            <li key={stage.key} className={`flex items-start gap-3 transition-opacity duration-500 ${state === "todo" ? "opacity-35" : "opacity-100"}`}>
              <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors duration-300 ${
                state === "done" ? "bg-ink text-white" : state === "active" ? "bg-sky-deep text-white" : "bg-ink/10 text-ink-soft"
              }`}>
                {state === "done" ? "✓" : index + 1}
              </span>
              <span>
                <span className="block font-semibold">{stage.title}</span>
                {state === "active" && <span className="block text-sm text-ink-soft mt-0.5">{stage.detail(status)}</span>}
              </span>
            </li>
          );
        })}
      </ol>

      <p className="mt-8 text-center text-sm text-ink-soft">
        This usually takes a minute or two. We'll also email you the link — safe to close this tab.
      </p>
    </div>
  );
}
